import React from "react";
import { Route } from "react-router-dom";
import AuthLayout from "./components/Layout/AuthLayout";
import DashboardLayout from "./layouts/DashboardLayout";
import HomePage from "./pages/HomePage";
import Target from "./pages/Content/Target";
import TrendAnalysisColors from "./pages/Content/TrendAnalysisColors";
import OutfitPostCount from "./pages/Content/OutfitPostCount";
import PostLatestDates from "./pages/Content/PostLatestDates";
import PostIngestionDates from "./pages/Content/PostIngestionDates";
import Industries from "./pages/Content/Industries";
import DailyViralFeed from "./pages/Feed/DailyViralFeed";
import InnovationFeed from "./pages/Feed/InnovationFeed";
import ContentGenerator from "./pages/GenerativeFeature/ContentGenerator";
import SentimentAnalyser from "./pages/GenerativeFeature/SentimentAnalyser";
import Invite from "./pages/Invite";
import Boards from "./pages/Boards";
import DesignerPage from "./pages/DesignerPage";
import FeatureTracking from "./pages/FeatureTracking";
import AcceptInvitation from "./pages/auth/AcceptInvitation";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import PrivateRoute from "./pages/auth/PrivateRoute";
import RoleBasedRoute from "./pages/auth/RoleBasedRoute";
import KreedalabsInternalRoute from "./pages/auth/KreedalabsInternalRoute";
import Login from "./pages/auth/Login";
import ClientSsoLanding from "./pages/auth/ClientSsoLanding";
import ClientOnlyRoute from "./pages/auth/ClientOnlyRoute";
import GenerativePlatform from "./pages/GenerativePlatform/GenerativePlatform";
import ImageGenerator from "./pages/GenerativePlatform/ImageGenerator";
import CollectionBuild from "./pages/GenerativePlatform/CollectionBuild";
import GenerativePlatformLibrary from "./pages/GenerativePlatform/GenerativePlatformLibrary";
import NotFound404 from "./pages/NotFound404";
import BoardImages from "./pages/Boards/BoardImages";
import Tickets from "./pages/Tickets";
import ReviewReport from "./components/Tickets/ReviewReport";
import ExpandViewOverallAnalysis from "./components/DesignerPage/TrendsSidebar/ExpandedViewOverallAnalysis";
import ExpandViewTrendCurves from "./components/DesignerPage/TrendsSidebar/ExpandedViewTrendCurves";
import TrendSnapshotDetails from "./components/DesignerPage/TrendsSnapshot/TrendSnapshotDetails";
import TrendSnapshot from "./pages/TrendSnapshot";
import Archive from "./pages/Archive";
import ExpandedViewColorAnalysis from "./components/DesignerPage/TrendsSidebar/ExpandedViewColorAnalysis";
import ExpandedViewSnapshotColorAnalysis from "./components/DesignerPage/TrendsSnapshot/ExpandedViewSnapshotColorAnalysis";
import ExpandedViewSnapShotOverallAnalysis from "./components/DesignerPage/TrendsSnapshot/ExpandedViewSnapShotOverallAnalysis";
import ExpandViewSnapshotTrendCurveAnalysis from "./components/DesignerPage/TrendsSnapshot/ExpandedViewSnapshotTrendCurves";

const routes = (
  isSidebarOpen: boolean,
  setIsSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>,
  trendsOpen: boolean,
  setTrendsOpen: React.Dispatch<React.SetStateAction<boolean>>
) => (
  <>
    <Route path="/auth" element={<AuthLayout />}>
      <Route path="login" element={<Login />} />
      <Route path="accept-invitation" element={<AcceptInvitation />} />
      <Route path="forgot-password" element={<ForgotPassword />} />
      <Route path="reset-password" element={<ResetPassword />} />
    </Route>

    <Route path="/generative" element={<ClientSsoLanding />} />

    <Route path="/" element={<HomePage />} />

    <Route
      path="/"
      element={
        <PrivateRoute>
          <DashboardLayout
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
          />
        </PrivateRoute>
      }
    >
      <Route
        path="content/target"
        element={
          <RoleBasedRoute
            roles={["admin"]}
            element={<Target isSidebarOpen={isSidebarOpen} />}
          />
        }
      />
      <Route
        path="internal/trend-analysis-colors"
        element={
          <KreedalabsInternalRoute
            roles={["admin"]}
            element={<TrendAnalysisColors isSidebarOpen={isSidebarOpen} />}
          />
        }
      />
      <Route
        path="internal/outfit-post-count"
        element={
          <KreedalabsInternalRoute
            roles={["admin"]}
            element={<OutfitPostCount isSidebarOpen={isSidebarOpen} />}
          />
        }
      />
      <Route
        path="internal/post-latest-dates"
        element={
          <KreedalabsInternalRoute
            roles={["admin"]}
            element={<PostLatestDates isSidebarOpen={isSidebarOpen} />}
          />
        }
      />
      <Route
        path="internal/post-ingestion-dates"
        element={
          <KreedalabsInternalRoute
            roles={["admin"]}
            element={<PostIngestionDates isSidebarOpen={isSidebarOpen} />}
          />
        }
      />
      <Route
        path="content/industries"
        element={<RoleBasedRoute roles={["admin"]} element={<Industries />} />}
      />
      <Route
        path="feed/daily-viral-feed"
        element={
          <RoleBasedRoute
            roles={["admin", "forecaster"]}
            element={<DailyViralFeed />}
          />
        }
      />
      <Route
        path="feed/innovation-feed"
        element={
          <RoleBasedRoute
            roles={["admin", "forecaster"]}
            element={<InnovationFeed />}
          />
        }
      />
      <Route
        path="generative-feature/content-generator"
        element={
          <RoleBasedRoute
            roles={["admin", "forecaster"]}
            element={<ContentGenerator isSidebarOpen={isSidebarOpen} />}
          />
        }
      />
      <Route
        path="generative-feature/sentiment-analyser"
        element={
          <RoleBasedRoute
            roles={["admin", "forecaster"]}
            element={<SentimentAnalyser isSidebarOpen={isSidebarOpen} />}
          />
        }
      />
      <Route
        path="generative-platform"
        element={
          <ClientOnlyRoute>
            <GenerativePlatform isSidebarOpen={isSidebarOpen} />
          </ClientOnlyRoute>
        }
      />
      <Route
        path="generative-platform/image-generator"
        element={
          <ClientOnlyRoute>
            <ImageGenerator isSidebarOpen={isSidebarOpen} />
          </ClientOnlyRoute>
        }
      />
      <Route
        path="generative-platform/collection-build"
        element={
          <ClientOnlyRoute>
            <CollectionBuild isSidebarOpen={isSidebarOpen} />
          </ClientOnlyRoute>
        }
      />
      <Route
        path="generative-platform/library"
        element={
          <ClientOnlyRoute>
            <GenerativePlatformLibrary isSidebarOpen={isSidebarOpen} />
          </ClientOnlyRoute>
        }
      />
      <Route
        path="boards"
        element={
          <RoleBasedRoute
            roles={["admin", "forecaster"]}
            element={<Boards isSidebarOpen={isSidebarOpen} />}
          />
        }
      />
      <Route
        path="boards/:boardId"
        element={
          <RoleBasedRoute
            roles={["admin", "forecaster"]}
            element={<BoardImages isSidebarOpen={isSidebarOpen} />}
          />
        }
      />

      <Route
        path="designer-page"
        element={
          <RoleBasedRoute
            roles={["admin", "forecaster"]}
            element={
              <DesignerPage
                isSidebarOpen={isSidebarOpen}
                trendsOpen={trendsOpen}
                setTrendsOpen={setTrendsOpen}
              />
            }
          />
        }
      />

      <Route
        path="designer-page/color-analysis"
        element={
          <RoleBasedRoute
            roles={["admin", "forecaster"]}
            element={
              <ExpandedViewColorAnalysis isSidebarOpen={isSidebarOpen} />
            }
          />
        }
      />
      <Route
        path="designer-page/overall-analysis"
        element={
          <RoleBasedRoute
            roles={["admin", "forecaster"]}
            element={
              <ExpandViewOverallAnalysis isSidebarOpen={isSidebarOpen} />
            }
          />
        }
      />
      <Route
        path="designer-page/archive"
        element={
          <RoleBasedRoute
            roles={["admin", "forecaster"]}
            element={<Archive isSidebarOpen={isSidebarOpen} />}
          />
        }
      />

      <Route
        path="designer-page/trend-curves"
        element={
          <RoleBasedRoute
            roles={["admin", "forecaster"]}
            element={<ExpandViewTrendCurves isSidebarOpen={isSidebarOpen} />}
          />
        }
      />
      <Route
        path="feature-tracking"
        element={
          <RoleBasedRoute roles={["admin"]} element={<FeatureTracking />} />
        }
      />
      <Route
        path="invite"
        element={
          <RoleBasedRoute
            roles={["admin"]}
            element={<Invite isSidebarOpen={isSidebarOpen} />}
          />
        }
      />
      <Route
        path="tickets"
        element={
          <RoleBasedRoute
            roles={["admin"]}
            element={<Tickets isSidebarOpen={isSidebarOpen} />}
          />
        }
      />
      <Route
        path="tickets/review/:id"
        element={
          <RoleBasedRoute
            roles={["admin"]}
            element={<ReviewReport isSidebarOpen={isSidebarOpen} />}
          />
        }
      />
      <Route
        path="trend-snapshot/details/:id"
        element={
          <RoleBasedRoute
            roles={["admin", "forecaster"]}
            element={
              <TrendSnapshotDetails
                isSidebarOpen={isSidebarOpen}
                trendsOpen={trendsOpen}
                setTrendsOpen={setTrendsOpen}
              />
            }
          />
        }
      />
      <Route
        path="trend-snapshot/details/color-analysis/:id"
        element={
          <RoleBasedRoute
            roles={["admin", "forecaster"]}
            element={
              <ExpandedViewSnapshotColorAnalysis
                isSidebarOpen={isSidebarOpen}
              />
            }
          />
        }
      />
      <Route
        path="trend-snapshot/details/overall-analysis/:id"
        element={
          <RoleBasedRoute
            roles={["admin", "forecaster"]}
            element={
              <ExpandedViewSnapShotOverallAnalysis
                isSidebarOpen={isSidebarOpen}
              />
            }
          />
        }
      />

      <Route
        path="trend-snapshot/details/Trend-Curve-analysis/:id"
        element={
          <RoleBasedRoute
            roles={["admin", "forecaster"]}
            element={
              <ExpandViewSnapshotTrendCurveAnalysis
                isSidebarOpen={isSidebarOpen}
              />
            }
          />
        }
      />
      <Route
        path="trend-snapshot"
        element={
          <RoleBasedRoute
            roles={["admin", "forecaster"]}
            element={<TrendSnapshot isSidebarOpen={isSidebarOpen} />}
          />
        }
      />
    </Route>

    <Route path="*" element={<NotFound404 />} />
  </>
);

export default routes;
